"use client";

import { useRef, useState, useEffect } from "react";
import { OptimizedImage } from "@/components/ui/optimized-image";
import Link from "next/link";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslations } from "next-intl";
import { getHotSpotsWithPriority, type HotSpot } from "@/config/locations";
import { placeSlug, KEYWORD_SLUG } from "@/lib/search/place-slug";

interface HotPlaceSectionProps {
  locale: string;
  /** 우선순위로 표시할 도시 ID (검색 결과의 주요 도시) */
  priorityCityId?: string;
  /** 레이아웃 모드: horizontal(가로 스크롤) 또는 vertical(세로 그리드) */
  layout?: "horizontal" | "vertical";
}

export default function HotPlaceSection({
  locale,
  priorityCityId,
  layout = "horizontal",
}: HotPlaceSectionProps) {
  const isVertical = layout === "vertical";
  const t = useTranslations("search");
  const hotSpots = getHotSpotsWithPriority(locale, priorityCityId, 15);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const update = () => {
      setAtStart(el.scrollLeft <= 1);
      setAtEnd(el.scrollLeft >= el.scrollWidth - el.clientWidth - 1);
    };

    update();
    el.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  const scroll = (direction: "left" | "right") => {
    const el = scrollRef.current;
    if (!el || !el.firstElementChild) return;

    const cardWidth = (el.firstElementChild as HTMLElement).offsetWidth + 12; // 12 = gap-3
    const amount = direction === "left" ? -cardWidth : cardWidth;
    el.scrollBy({ left: amount, behavior: "smooth" });
  };

  if (hotSpots.length === 0) {
    return null;
  }

  const getSpotName = (spot: HotSpot) => {
    const localeKey = locale as keyof typeof spot.name;
    return spot.name[localeKey] || spot.name.ko || spot.name.en;
  };

  const getSearchUrl = (spot: HotSpot) => {
    // 랜드마크(핫스팟)는 좁은 bounds 기반 → scope=district. slug는 로마자(표시무관, bounds가 결과 결정).
    const slug = placeSlug(spot.name.en || spot.name.ko) || KEYWORD_SLUG;
    const offset = 0.015; // 약 1.5km
    const { latitude, longitude } = spot.coordinates;
    const params = new URLSearchParams({
      scope: "district",
      ne_lat: String(latitude + offset),
      ne_lng: String(longitude + offset),
      sw_lat: String(latitude - offset),
      sw_lng: String(longitude - offset),
    });
    return `/${locale}/s/${slug}?${params.toString()}`;
  };

  return (
    <section className="pb-12">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold">
            {t("recommendations.hotPlaces")}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {t("recommendations.hotPlacesDescription")}
          </p>
        </div>
        {/* 데스크탑에서만 좌우 버튼 표시 (horizontal 모드일 때만) */}
        {!isVertical && (
          <div className="hidden md:flex gap-2">
            <button
              onClick={() => scroll("left")}
              disabled={atStart}
              className="p-2 border border-gray-300 rounded-full transition disabled:opacity-30 disabled:cursor-not-allowed hover:enabled:shadow-md"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => scroll("right")}
              disabled={atEnd}
              className="p-2 border border-gray-300 rounded-full transition disabled:opacity-30 disabled:cursor-not-allowed hover:enabled:shadow-md"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      {/* 레이아웃에 따른 컨테이너 */}
      {isVertical ? (
        <div className="grid grid-cols-3 gap-3">
          {hotSpots.map((spot) => (
            <Link
              key={spot.id}
              href={getSearchUrl(spot)}
              className="group relative aspect-square overflow-hidden shadow-sm hover:shadow-md transition-shadow"
              style={{
                borderTopLeftRadius: "50%",
                borderTopRightRadius: "50%",
                borderBottomLeftRadius: "5px",
                borderBottomRightRadius: "50%",
              }}
            >
              <OptimizedImage
                src={spot.imageUrl}
                alt={getSpotName(spot)}
                fill
                sizes="200px"
                className="object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-linear-to-t from-black via-black/20 to-transparent" />
              <div className="absolute bottom-2 left-2">
                <p className="text-white font-semibold text-sm truncate">
                  {getSpotName(spot)}
                </p>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div
          ref={scrollRef}
          className="flex gap-3 overflow-x-auto scrollbar-hide pb-2 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:mx-0 lg:px-0"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {hotSpots.map((spot) => (
            <Link
              key={spot.id}
              href={getSearchUrl(spot)}
              className="group relative shrink-0 w-[calc((100%-24px)/2)] sm:w-[calc((100%-36px)/4)] md:w-[calc((100%-48px)/5)] lg:w-[calc((100%-60px)/6)] 3xl:w-[calc((100%-72px)/7)] aspect-square rounded-3xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
              style={{
                borderTopLeftRadius: "50%",
                borderTopRightRadius: "50%",
                borderBottomLeftRadius: "5px",
                borderBottomRightRadius: "50%",
              }}
            >
              <OptimizedImage
                src={spot.imageUrl}
                alt={getSpotName(spot)}
                fill
                sizes="200px"
                className="object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-linear-to-t from-black via-black/20 to-transparent" />
              <div className="absolute bottom-3 left-4">
                <p className="text-white font-semibold text-sm sm:text-base truncate">
                  {getSpotName(spot)}
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}
